/* -----------------UI--------------- */
import { Card, Divider } from "react-native-paper";

import { useTranslation } from "react-i18next";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import { CustomAppBar } from "../CustomAppbar";
import type { CustomAppBarProps } from "../CustomAppbar/types";
import { CustomListSubheader } from "../CustomListSubheader";
import { Filters } from "../FilterView";
import ScrollContainer from "../ScrollContainer";
import { ProductsList } from "./ProductsList";

/* -----------------Types--------------- */
import type { ExamDetails } from "@memorang/types";
import type { Section } from "./ProductsList";

/* -----------------Props--------------- */
type Props = {
	products: Section[];
	handleSwitchExamContext: (data: ExamDetails) => void;
	searchQuery: string;
	handleChangeSearch: (text: string) => void;
	selectedFilters: string[];
	handleSelectFilter: (filter: string) => void;
	filters: string[];
	customAppBar: CustomAppBarProps;
	NumItemsSubheader?: React.ComponentType<{ numItems: number }>;
	handleOpenProductModal: (bundleId: string, bundleType: string) => void;
	currentExamId?: string | number;
};

/* -----------------Component--------------- */
const Store = (props: Props) => {
	const {
		products,
		handleSwitchExamContext,
		selectedFilters,
		handleSelectFilter,
		filters,
		searchQuery,
		handleChangeSearch,
		customAppBar,
		NumItemsSubheader,
		handleOpenProductModal,
		currentExamId,
	} = props;

	const { t } = useTranslation();

	const { isMobile } = useDevice();

	const showFilters = filters.length > 1;

	const numItems = products.reduce((acc, section) => {
		return acc + section.data.length;
	}, 0);

	const DefaultNumItemsSubheader = ({ numItems }: { numItems: number }) => {
		const numItemsText = t("common.examCount", { count: numItems });
		return (
			<CustomListSubheader enableGutters>{numItemsText}</CustomListSubheader>
		);
	};

	const FinalNumItemsSubheader = NumItemsSubheader ?? DefaultNumItemsSubheader;

	const handleClearSearch = () => {
		handleChangeSearch("");
	};

	return (
		<>
			<CustomAppBar {...customAppBar} />
			<Box flex={1}>
				<ScrollContainer
					contentContainerStyle={{
						paddingBottom: 100,
						paddingTop: 16,
						paddingHorizontal: isMobile ? 0 : 16,
					}}
					// biome-ignore lint/a11y/useSemanticElements: <explanation>
					role="main"
				>
					<Box gap={16} paddingHorizontal={isMobile ? 16 : 0}>
						{showFilters && (
							<Filters
								filters={filters}
								handleSelectFilter={handleSelectFilter}
								selectedFilters={selectedFilters}
							/>
						)}
						<Box>
							{showFilters && <Divider />}
							<FinalNumItemsSubheader numItems={numItems} />
						</Box>
					</Box>

					{isMobile ? (
						<ProductsList
							handleOpenProductModal={handleOpenProductModal}
							products={products}
							handleSwitchExamContext={handleSwitchExamContext}
							handleClearSearch={handleClearSearch}
							searchQuery={searchQuery}
							currentExamId={currentExamId ? String(currentExamId) : undefined}
						/>
					) : (
						<Card
							style={{
								overflow: "hidden",
							}}
						>
							<ProductsList
								handleOpenProductModal={handleOpenProductModal}
								products={products}
								handleSwitchExamContext={handleSwitchExamContext}
								handleClearSearch={handleClearSearch}
								searchQuery={searchQuery}
								currentExamId={
									currentExamId ? String(currentExamId) : undefined
								}
							/>
						</Card>
					)}
				</ScrollContainer>
			</Box>
		</>
	);
};

export default Store;
export type { Section } from "./ProductsList";
